import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, IconButton, Divider, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Alert, Tooltip } from '@mui/material';
import { Icon } from '@iconify/react';
import { getPasswordResetHistoryByUserId, PasswordResetHistoryEntry } from '../utils/passwordResetHistory';
interface PasswordResetHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  userId?: string;
  userName?: string;
}
const PasswordResetHistoryDrawer: React.FC<PasswordResetHistoryDrawerProps> = ({
  open,
  onClose,
  userId,
  userName
}) => {
  const [history, setHistory] = useState<PasswordResetHistoryEntry[]>([]);
  useEffect(() => {
    if (open && userId) {
      const userHistory = getPasswordResetHistoryByUserId(userId);
      setHistory(userHistory);
    }
  }, [open, userId]);
  const getResetByColor = (resetBy: string) => {
    switch (resetBy) {
      case 'BO_ADMIN':
        return {
          bg: 'rgba(79, 70, 229, 0.1)',
          color: '#4F46E5'
        };
      case 'SYSTEM':
        return {
          bg: 'rgba(34, 211, 238, 0.1)',
          color: '#22D3EE'
        };
      case 'USER':
        return {
          bg: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981'
        };
      default:
        return {
          bg: 'rgba(148, 163, 184, 0.1)',
          color: '#94A3B8'
        };
    }
  };
  const getDeliveryStatusColor = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return {
          bg: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981'
        };
      case 'FAILED':
        return {
          bg: 'rgba(239, 68, 68, 0.1)',
          color: '#EF4444'
        };
      case 'PENDING':
        return {
          bg: 'rgba(245, 158, 11, 0.1)',
          color: '#F59E0B'
        };
      default:
        return {
          bg: 'rgba(148, 163, 184, 0.1)',
          color: '#94A3B8'
        };
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        sm: 600,
        md: 700
      },
      p: 0
    }
  }} data-id="element-1779" data-mp="1wfnspmx6idgt">
      <Box sx={{
      p: 3,
      borderBottom: '1px solid #E5E7EB'
    }} data-id="element-1780" data-mp="2t43jfmc0ysvi">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }} data-id="element-1781" data-mp="2fbdvhccxrdvr">
          <Box data-id="element-1782" data-mp="373mp88hd5s4n">
            <Typography variant="h6" fontWeight={600} data-id="element-1783" data-mp="37333shsi8llv">
              Password Reset History
            </Typography>
            {userName && <Typography variant="body2" color="text.secondary" mt={0.5} data-id="element-1784" data-mp="3bvmhder6zgqo">
                User: {userName}
              </Typography>}
          </Box>
          <IconButton onClick={onClose} size="small" data-id="element-1785" data-mp="vh19ud9zebu9">
            <Icon icon="lucide:x" width={18} height={18} data-id="element-1786" data-mp="vef32fux00d3" />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{
      p: 3,
      overflowY: 'auto',
      height: 'calc(100vh - 100px)'
    }} data-id="element-1787" data-mp="1a708si9d1ras">
        {history.length === 0 ? <Alert severity="info" data-id="element-1788" data-mp="hh07suhzcyd7">
            No password reset history found for this user.
          </Alert> : <TableContainer component={Paper} elevation={0} data-id="element-1789" data-mp="cjrp8ssx6sbf">
            <Table size="small" data-id="element-1790" data-mp="rffr4barn3c5">
              <TableHead data-id="element-1791" data-mp="rcl8mpbbbsld">
                <TableRow data-id="element-1792" data-mp="mfuptu84iiw9">
                  <TableCell data-id="element-1793" data-mp="mejm1ueohb55">
                    <Typography variant="subtitle2" fontWeight={600} data-id="element-1794" data-mp="me9w26tmv3zg">
                      Timestamp
                    </Typography>
                  </TableCell>
                  <TableCell data-id="element-1795" data-mp="1xtos98e0prti">
                    <Typography variant="subtitle2" fontWeight={600} data-id="element-1796" data-mp="1xugpsor6dzmf">
                      Reset By
                    </Typography>
                  </TableCell>
                  <TableCell data-id="element-1797" data-mp="cbxvbzg7vohk">
                    <Typography variant="subtitle2" fontWeight={600} data-id="element-1798" data-mp="cczgiwqy5rd0">
                      Delivery Status
                    </Typography>
                  </TableCell>
                  <TableCell data-id="element-1799" data-mp="legt4555bawy">
                    <Typography variant="subtitle2" fontWeight={600} data-id="element-1800" data-mp="lep6rz93ek97">
                      Details
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody data-id="element-1801" data-mp="v9h36zqxv12e">
                {history.map(entry => {
              const resetByStyle = getResetByColor(entry.resetBy);
              const deliveryStyle = getDeliveryStatusColor(entry.deliveryStatus);
              return <TableRow key={entry.id} data-id="element-1802" data-mp="190vpbfg57ido">
                      <TableCell data-id="element-1803" data-mp="1e19yj0thcyda">
                        <Typography variant="body2" fontSize="0.8rem" data-id="element-1804" data-mp="1e2l4966hkg71">
                          {formatDate(entry.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell data-id="element-1805" data-mp="1j5c4s7ppmxgy">
                        <Chip label={entry.resetBy.replace('_', ' ')} size="small" sx={{
                    backgroundColor: resetByStyle.bg,
                    color: resetByStyle.color,
                    fontWeight: 600,
                    fontSize: '0.7rem'
                  }} data-id="element-1806" data-mp="1e7u414lujok1" />
                        {entry.resetByName && <Typography variant="caption" display="block" color="text.secondary" mt={0.5} data-id="element-1807" data-mp="1x4lgov6vv5f4">
                            {entry.resetByName}
                          </Typography>}
                      </TableCell>
                      <TableCell data-id="element-1808" data-mp="izicm2esnuzy">
                        <Tooltip title={entry.deliveryError || entry.emailMessageId || ''} arrow data-id="element-1809" data-mp="nxjx8rrvc2dj">
                          <Chip label={entry.deliveryStatus} size="small" sx={{
                      backgroundColor: deliveryStyle.bg,
                      color: deliveryStyle.color,
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }} data-id="element-1810" data-mp="e6yqgh21jrjj" />
                        </Tooltip>
                        {entry.deliveryStatus === 'SUCCESS' && entry.emailMessageId && <Typography variant="caption" display="block" color="text.secondary" mt={0.5} data-id="element-1811" data-mp="1pm45mkjczik1">
                              ID: {entry.emailMessageId.substring(0, 16)}...
                            </Typography>}
                      </TableCell>
                      <TableCell data-id="element-1812" data-mp="3f2zo8aca7o94">
                        {entry.reason && <Typography variant="body2" fontSize="0.8rem" mb={0.5} data-id="element-1813" data-mp="3sue78h66vpg6">
                            <strong data-id="element-1814" data-mp="6nfwjfr9edw6">Reason:</strong> {entry.reason}
                          </Typography>}
                        <Typography variant="body2" fontSize="0.8rem" color="text.secondary" data-id="element-1815" data-mp="1nbhlu601r82">
                          Email: {entry.recipientEmail}
                        </Typography>
                        {entry.deliveryError && <Typography variant="body2" fontSize="0.8rem" color="error" mt={0.5} data-id="element-1816" data-mp="1mm6lc87enjoc">
                            <Icon icon="lucide:alert-circle" width={12} height={12} style={{
                      marginRight: 4
                    }} data-id="element-1817" data-mp="1wbyjtdimkwy3" />
                            {entry.deliveryError}
                          </Typography>}
                      </TableCell>
                    </TableRow>;
            })}
              </TableBody>
            </Table>
          </TableContainer>}
      </Box>
    </Drawer>;
};
export default PasswordResetHistoryDrawer;